import { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';

// ==================== MUI Material & External Libraries ====================
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    circularProgressClasses,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputLabel,
    Stack,
    Switch,
    TextField,
    Typography,
    useTheme,
    LinearProgress
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';

// ==================== Rich Text Editor ====================
import QuillEditor from 'components/third-party/QuillEditor';

// ==================== Icons ====================
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';

// ==================== Project Imports ====================
import CompressedFilemanager from 'components/CommonFileManager/CompressedFileManager';
import UserLookup from 'sections/apps/users/UserLookup';
import TatChip from 'components/TaskManagement/TatChip';
import { FileManagerModule } from 'types/CommonFileManager/commonFileManager';
import { TaskStage, TaskFormProps, getProjectFlagsForStage, TaskPriorityEnum, TaskStatusEnum, getPriorityName, getStatusName, getStageNameFromId, getStageId, getPriorityEnumVal, getStatusEnumVal } from 'types/taskManagement/TaskManagement';
import axios from 'axios';
import { useInsertTask, useUpdateTask, useGetTaskModal, useGetTaskFiles, useGenerateUploadFileUrls, useAfterSuccessfulFileUpload, mutateTaskFiles, useDeleteTaskFile } from 'api/TaskManagement/taskManagement';
import { useUpdateProjectStageSummary } from 'api/PPFUser/ppfProject';
import { GetFormattedDateOnly, GetFormattedDateTime, GetFormattedTimeOnly, minDate, minDateFormated } from 'utils/commonFunctions';
import { showSuccess } from 'utils/errorHandler';
import {
    ModerProjectNameIcon,
    PendingChipIcon,
    InprogressChipIcon,
    CompleteChipIcon,
    UrgentFlagIcon,
    HighFlagIcon,
    PriorityFlagIcon,
    CustomUncheckedIcon,
    CustomCheckedIcon
} from 'utils/TaskManagementIcon/taskmanagemeneticon';
import { mutate } from 'swr';

// Helper function to handle full Task File Upload workflow directly in TaskForm
const uploadTaskFiles = async (taskID: number, files: File[], onProgress?: (fileName: string, percent: number) => void) => {
    if (!files || files.length === 0) return;

    const fileNames = files.map((file) => file.name);

    // Step 1: Generate Upload File URLs
    const res = await useGenerateUploadFileUrls({ taskID, fileNames });
    const urlList: any[] = Array.isArray(res) ? res : (res?.data || res?.result || []);

    // Step 2: Upload each file to Azure Storage via uploadFileUrl sequentially (one by one like PermitForm)
    for (const file of files) {
        const item = urlList.find((u) => {
            if (!u) return false;
            const uName = String(u.fileName || u.name || u.uploadFileName || '').toLowerCase();
            const fName = file.name.toLowerCase();
            return uName === fName || decodeURIComponent(uName) === decodeURIComponent(fName);
        }) || (urlList.length === 1 ? urlList[0] : null);

        const uploadFileUrl = item?.uploadFileUrl || null;

        if (uploadFileUrl) {
            await axios.put(uploadFileUrl, file, {
                headers: {
                    'Content-Type': file.type || 'application/octet-stream',
                    'x-ms-blob-type': 'BlockBlob'
                },
                timeout: 0,
                onUploadProgress: (progressEvent) => {
                    if (onProgress) {
                        const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || file.size));
                        onProgress(file.name, percent);
                    }
                }
            });

            // Step 3: Register uploaded file in DB
            try {
                await useAfterSuccessfulFileUpload({ taskID, fileName: item?.blobFileName || item?.fileName });
            } catch (err) {
                console.error('After successful file upload call failed:', err);
            }
        }
    }

    // Step 4: Refresh cache once after all uploads finish
    mutateTaskFiles(taskID);
};

// Helper function to handle Task File Deletion workflow directly in TaskForm
const deleteTaskFiles = async (taskID: number | string, deletedFileNames: string[], existingFiles?: any[]) => {
    if (!deletedFileNames || deletedFileNames.length === 0 || !existingFiles || !Array.isArray(existingFiles)) return;

    for (const deletedName of deletedFileNames) {
        const cleanName = String(deletedName || '').replace(/^files\//, '').toLowerCase();
        const deletedBaseName = cleanName.split('/').pop() || cleanName;

        const matchedFile = existingFiles.find((f: any) => {
            const fName = String(f.fileName || f.name || f.filename || '').toLowerCase();
            const fId = String(f.fileID || f.id || f.fileId || '');
            const fBaseName = fName.split('/').pop() || fName;

            return (
                fName === cleanName ||
                fName === String(deletedName).toLowerCase() ||
                fBaseName === deletedBaseName ||
                fId === String(deletedName) ||
                fId === cleanName
            );
        });

        if (matchedFile && (matchedFile.fileID || matchedFile.id)) {
            const targetFileID = String(matchedFile.fileID || matchedFile.id);
            try {
                await useDeleteTaskFile({ taskID, fileID: targetFileID });
            } catch (err) {
                console.error('Task file deletion failed:', err);
            }
        }
    }

    // Refresh cache once after all deletions finish
    mutateTaskFiles(taskID);
};




const LAST_ASSIGNED_USERS_PREFIX = 'last_assigned_task_users_module_';
const LAST_ASSIGNED_USERS_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

const getLastAssignedUsers = (moduleId: number | string = 0): any[] => {
    try {
        const key = `${LAST_ASSIGNED_USERS_PREFIX}${moduleId}`;
        const cached = localStorage.getItem(key);
        if (!cached) return [];
        const parsed = JSON.parse(cached);
        if (parsed?.expiresAt && Date.now() < parsed.expiresAt) {
            return Array.isArray(parsed.assignedUserIDs) ? parsed.assignedUserIDs : [];
        }
        localStorage.removeItem(key);
        return [];
    } catch {
        return [];
    }
};

const setLastAssignedUsers = (assignedUserIDs: any[], moduleId: number | string = 0) => {
    try {
        if (Array.isArray(assignedUserIDs) && assignedUserIDs.length > 0) {
            const key = `${LAST_ASSIGNED_USERS_PREFIX}${moduleId}`;
            const dataToStore = {
                assignedUserIDs,
                expiresAt: Date.now() + LAST_ASSIGNED_USERS_EXPIRY_MS
            };
            localStorage.setItem(key, JSON.stringify(dataToStore));
        }
    } catch (e) {
        console.error('Failed to cache assigned users:', e);
    }
};

const getPlainText = (html: string) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return (tempDiv.textContent || tempDiv.innerText || '').replace(/\u00a0/g, ' ');
};

// Dropdown Options Setup
const priorityOptions = [
    { value: 'Urgent', label: 'Urgent', icon: <HighFlagIcon /> },
    { value: 'High', label: 'High', icon: <UrgentFlagIcon /> },
    { value: 'Normal', label: 'Normal', icon: <PriorityFlagIcon color="#1677FF" /> },
    { value: 'Low', label: 'Low', icon: <PriorityFlagIcon color="#8C8C8C" /> }

];

const statusOptions = [
    { value: 'Pending', label: 'Pending', icon: <PendingChipIcon /> },
    { value: 'In-Progress', label: 'In-Progress', icon: <InprogressChipIcon /> },
    { value: 'Completed', label: 'Completed', icon: <CompleteChipIcon /> },
    { value: 'Cancelled', label: 'Cancelled', icon: <CloseOutlined style={{ fontSize: 13, color: '#ff4d4f' }} /> }
];

const stageOptions = Object.values(TaskStage).filter((stage) => stage !== TaskStage.ALL);

const TaskSchema = Yup.object().shape({
    title: Yup.string().trim().required('Title is required'),
    dueDate: Yup.mixed<Dayjs>()
        .nullable()
        .required('Due date is required')
        .test(
            'is-valid-date',
            'Please enter a valid due date',
            (value) => {
                if (!value) return true;
                return dayjs(value).isValid();
            }
        )
        .test(
            'min-date',
            `Due date cannot be earlier than ${minDateFormated}`,
            (value) => {
                if (!value) return true;
                if (!dayjs(value).isValid()) return true;

                return !dayjs(value).isBefore(dayjs(minDate), 'day');
            }
        ),
    dueTime: Yup.mixed()
        .nullable()
        .required('Due time is required')
        .test('is-valid-time', 'Due time is required', (val) => Boolean(val && dayjs(val as any).isValid())),
    priority: Yup.string().nullable(),
    status: Yup.string().nullable(),
    assignedTo: Yup.mixed().nullable(),
    stage: Yup.string().nullable(),
    visibleToContractor: Yup.boolean(),
    projectFlags: Yup.object().shape({
        noc: Yup.boolean(),
        ntbo: Yup.boolean(),
        pp: Yup.boolean(),
        coc: Yup.boolean()
    }),
});

export default function TaskForm({ open, closeModal, task, projectName, onSave, stageName }: TaskFormProps) {

    const { projectID } = useParams();
    const location = useLocation();
    const isPlanReview = location.pathname.toLowerCase().includes('planreviews');
    const projectIdNumber = projectID ? Number(projectID) : null;
    const isEditMode = Boolean(task);
    const storedProjectName = sessionStorage.getItem('activeProjectName');
    const targetTaskId = task?.taskID || null;
    const { data: taskDetail, isLoading: isLoadingTaskDetail } = useGetTaskModal(isEditMode && targetTaskId ? targetTaskId : null);

    const rawProjectID = projectIdNumber || (taskDetail as any)?.projectID || (taskDetail as any)?.projectId || (task as any)?.projectID || (task as any)?.projectId;
    const hasProject = Boolean(rawProjectID && Number(rawProjectID) > 0) || Boolean(projectName);
    const activeProjectName = hasProject ? (storedProjectName || (taskDetail as any)?.projectName || (task as any)?.projectName || projectName || '') : '';

    const taskModule = hasProject ? (isPlanReview ? 2 : 1) : Number((taskDetail as any)?.module ?? (task as any)?.module ?? 0);
    // ==================== Task File Management State ====================
    const { data: existingFiles } = useGetTaskFiles(isEditMode && targetTaskId ? targetTaskId : null);

    // Format initial files for CompressedFileManager display
    const formattedInitialFiles = useMemo(() => {
        if (!existingFiles || !Array.isArray(existingFiles)) return [];
        return existingFiles.map((f: any) => ({
            name: f.fileName || f.name,
            size: f.fileSize || f.size || 0,
            fileID: f.fileID || f.id
        }));
    }, [existingFiles]);

    const theme = useTheme();
    // Ref to access CompressedFileManager local state (added/deleted files)
    const fileManagerRef = useRef<any>(null);
    const [isReady, setIsReady] = useState(false);
    // State to hold pending files selected for upload
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [hasFileChanges, setHasFileChanges] = useState<boolean>(false);
    const activeTaskIdRef = useRef<number | null>(null);
    const isDark = theme.palette.mode === 'dark';
    const showLoading = !isReady || (isEditMode && isLoadingTaskDetail);

    const initialValues = useMemo(
        () => {
            const source = isEditMode ? (taskDetail || task) : null;

            const title = source?.taskTitle || source?.title || '';
            const rawDueDate = source?.dueOn || source?.dueDate;
            const cleanDueDate = typeof rawDueDate === 'string'
                ? rawDueDate.replace(/Z$/i, '').replace(/[+-]\d{2}:\d{2}$/, '')
                : rawDueDate;
            const dueDate = cleanDueDate ? dayjs(cleanDueDate) : null;
            const dueTime = cleanDueDate ? dayjs(cleanDueDate) : (!isEditMode ? dayjs().hour(17).minute(0).second(0).millisecond(0) : null);
            const priority = getPriorityName(source?.priority) || 'Normal';
            const status = getStatusName(source?.status || source?.taskStatus) || 'Pending';
            const assignedTo = isEditMode
                ? (source?.assignedUserIDs || source?.assignedUsers?.map((u: any) => u.userID) || [])
                : getLastAssignedUsers(taskModule);
            const stage = stageName || source?.stageName || getStageNameFromId(source?.stageID) || source?.stage || TaskStage.INTAKE;
            const visibleToContractor = source?.visibleToContractor ?? false;
            const projectFlags = {
                noc: source?.projectFlags?.noc ?? source?.noc ?? false,
                ntbo: source?.projectFlags?.ntbo ?? source?.ntbo ?? false,
                pp: source?.projectFlags?.pp ?? source?.pp ?? false,
                coc: source?.projectFlags?.coc ?? source?.coc ?? source?.coC ?? false
            };
            const description = source?.descrFormatted || source?.description || '';

            return {
                title,
                dueDate,
                dueTime,
                priority,
                status,
                assignedTo,
                stage,
                visibleToContractor,
                projectFlags,
                description
            };
        },
        [isEditMode, task, taskDetail, stageName, open, taskModule]
    );

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema: TaskSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            try {
                let combined = dayjs(values.dueDate);
                if (combined.isValid() && values.dueTime) {
                    const timePart = dayjs(values.dueTime);
                    if (timePart.isValid()) {
                        combined = combined
                            .hour(timePart.hour())
                            .minute(timePart.minute())
                            .second(timePart.second())
                            .millisecond(timePart.millisecond());
                    }
                }
                const dueOn = combined.isValid() ? combined.format('YYYY-MM-DD HH:mm:ss') : null;

                const finalProjectID = projectIdNumber || (taskDetail as any)?.projectID || (taskDetail as any)?.projectId || (task as any)?.projectID || (task as any)?.projectId || null;

                const payload = {
                    taskTitle: values.title,
                    projectID: finalProjectID ? Number(finalProjectID) : null,
                    stageID: isPlanReview ? 3 : getStageId(values.stage),
                    taskModule,
                    priority: getPriorityEnumVal(values.priority),
                    taskStatus: getStatusEnumVal(values.status),
                    dueOn,
                    visibleToContractor: values.visibleToContractor,
                    descrPlain: getPlainText(values.description || ''),
                    descrFormatted: values.description || '',
                    noc: values.projectFlags.noc,
                    ntbo: values.projectFlags.ntbo,
                    pp: values.projectFlags.pp,
                    coc: values.projectFlags.coc,
                    coC: values.projectFlags.coc,
                    assignedUserIDs: Array.isArray(values.assignedTo) ? values.assignedTo : []
                };

                let createdTaskId = Number(task?.taskID);

                if (isEditMode) {
                    const res = await useUpdateTask({
                        ...payload,
                        taskID: Number(task?.taskID),
                        updatedOn: taskDetail?.updatedOn || (task as any)?.updatedOn || null
                    });
                    showSuccess('Task updated successfully!');
                    if (finalProjectID && location.pathname.includes('ppf/projects/workspace')) {
                        const stageIDs = [...new Set([
                            ...(payload.stageID !== taskDetail.stageID ? [taskDetail.stageID] : []),
                            payload.stageID,
                            res?.additionalResponseData?.NextInProgessTask?.StageID
                        ].filter(Boolean))];

                        useUpdateProjectStageSummary(stageIDs, finalProjectID)
                    }
                    if (finalProjectID && location.pathname.includes('ppf/planReviews')) {
                        await mutate(`Project/PlanReview/GetTaskSummary/${finalProjectID}`);
                    }
                    activeTaskIdRef.current = createdTaskId;
                } else {
                    const res = await useInsertTask(payload);
                    createdTaskId = Number(res?.primaryKeyID || res?.taskID || res?.id);
                    if (Array.isArray(values.assignedTo) && values.assignedTo.length > 0) {
                        setLastAssignedUsers(values.assignedTo, taskModule);
                    }
                    showSuccess('Task created successfully!');
                    if (finalProjectID && location.pathname.includes('ppf/projects/workspace')) {
                        const nextStageID = res?.additionalResponseData?.NextInProgessTask?.StageID || null

                        const stageIDs = nextStageID ? [payload.stageID!, nextStageID] : [payload.stageID!]
                        useUpdateProjectStageSummary([...new Set(stageIDs)], finalProjectID)
                    }
                    if (finalProjectID && location.pathname.includes('ppf/planReviews')) {
                        await mutate(`Project/PlanReview/GetTaskSummary/${finalProjectID}`);
                    }
                    activeTaskIdRef.current = createdTaskId;
                }

                if (location.pathname === '/ppf/taskList') {
                    mutate((key: any) => typeof key === 'string' ? key.includes('Task/GetList') : Array.isArray(key) && key[0]?.includes('Task/GetList'));
                }

                mutate((key: any) => {
                    if (!key) return false;
                    const keyStr = typeof key === 'string' ? key : (Array.isArray(key) ? key[0] : JSON.stringify(key));
                    return keyStr.includes('/TaskBoard') || keyStr.includes('Task/GetPanelList');
                });

                // ==================== Task File Deletion & Upload Handling ====================
                // Get staged files & deletions from CompressedFileManager ref
                const localState = fileManagerRef.current?.getLocalState?.();
                const filesToUpload: File[] = (localState?.added && localState.added.length > 0) ? localState.added : pendingFiles;
                const deletedFileNames: string[] = localState?.deleted || [];

                if (fileManagerRef.current?.save && createdTaskId) {
                    await fileManagerRef.current.save(createdTaskId);
                } else if (createdTaskId) {
                    if (deletedFileNames.length > 0) {
                        await deleteTaskFiles(createdTaskId, deletedFileNames, existingFiles);
                    }
                    if (filesToUpload.length > 0) {
                        await uploadTaskFiles(createdTaskId, filesToUpload);
                    }
                }

                mutateTaskFiles(createdTaskId);

                if (onSave) {
                    await onSave(values as any);
                }
                resetForm();
                closeModal();
            } catch (err) {
                console.error('Task submit error:', err);
            } finally {
                setSubmitting(false);
            }
        }
    });

    const { values, errors, touched, setFieldTouched, handleSubmit, isSubmitting, setFieldValue, handleChange, handleBlur, dirty } = formik;

    useEffect(() => {
        if (open) {
            setHasFileChanges(false);
            setPendingFiles([]);
            formik.resetForm({ values: initialValues });
            const timer = setTimeout(() => {
                setIsReady(true);
            }, 60);
            return () => clearTimeout(timer);
        } else {
            setIsReady(false);
            setHasFileChanges(false);
            setPendingFiles([]);
        }
    }, [open, isEditMode, task, taskDetail]);

    return (
        <Dialog open={open}
            onClose={(event, reason) => {
                if (isSubmitting) return;
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                    closeModal();
                }
            }}

            maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 1, display: 'flex', flexDirection: 'column', maxHeight: '90vh' } }}>
            {/* Dialog Header */}
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" component="span">
                        {isEditMode ? (
                            <>
                                <Typography component="span" sx={{ color: 'primary.main' }}>
                                    Edit
                                </Typography>{' '}
                                Task
                            </>
                        ) : (
                            <>
                                <Typography component="span" sx={{ color: 'primary.main' }}>
                                    Add
                                </Typography>{' '}
                                Task
                            </>
                        )}
                    </Typography>

                    {hasProject && activeProjectName && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 0.5 }}>
                            <Typography variant="h6" component="span" color="text.secondary">
                                -
                            </Typography>
                            <Box
                                sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    bgcolor: 'primary.lighter',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <ModerProjectNameIcon style={{ width: 14, height: 14 }} />
                            </Box>
                            <Typography title={"Project Name: " + activeProjectName} variant="h6" component="span" fontWeight={600} color="text.primary">
                                {activeProjectName}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
                <IconButton
                    aria-label="close"
                    disabled={isSubmitting}
                    onMouseDown={(e) => {
                        if (isSubmitting) return;
                        e.preventDefault();
                        closeModal();
                    }}
                    sx={{
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'primary.lighter' }
                    }}
                >
                    <CloseOutlined />
                </IconButton>
            </DialogTitle>

            <Divider />

            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
                    <DialogContent sx={{ p: 3, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                        {showLoading ? (
                            <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1, py: 10 }}>
                                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                    <CircularProgress
                                        variant="determinate"
                                        sx={{ color: 'grey.200' }}
                                        size={44}
                                        thickness={4}
                                        value={100}
                                    />
                                    <CircularProgress
                                        variant="indeterminate"
                                        disableShrink
                                        sx={{
                                            color: 'primary.main',
                                            animationDuration: '650ms',
                                            position: 'absolute',
                                            left: 0,
                                            [`& .${circularProgressClasses.circle}`]: {
                                                strokeLinecap: 'round'
                                            }
                                        }}
                                        size={44}
                                        thickness={4}
                                    />
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontWeight: 500 }}>
                                    Loading.......
                                </Typography>
                            </Stack>
                        ) : (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Grid container spacing={2.5}>
                                    {/* Title */}
                                    <Grid size={{ xs: 12 }}>
                                        <Stack spacing={0.75}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <InputLabel htmlFor="task-title" required>
                                                    Task Name
                                                </InputLabel>
                                                {isEditMode && (
                                                    <TatChip
                                                        variant="text"
                                                        startedOn={(taskDetail || task)?.startedOn}
                                                        completedOn={(taskDetail || task)?.completedOn}
                                                        elapsedMinutes={(taskDetail || task)?.elapsedMinutes}
                                                        showIcon={false}
                                                    />
                                                )}
                                            </Stack>
                                            <TextField
                                                id="task-title"
                                                name="title"
                                                placeholder="Enter Task Name"
                                                fullWidth
                                                disabled={isSubmitting}
                                                value={values.title}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={Boolean(touched.title && errors.title)}
                                                helperText={touched.title ? (errors.title as string) : undefined}
                                                slotProps={{ htmlInput: { maxLength: 1000 } }} />
                                        </Stack>
                                    </Grid>

                                    {/* Due Date & Time */}
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <Stack spacing={0.75}>
                                            <InputLabel required>Due Date</InputLabel>
                                            <DatePicker
                                                value={values.dueDate}
                                                disabled={isSubmitting}
                                                onChange={(newValue) => {
                                                    setFieldValue('dueDate', newValue)
                                                    setFieldTouched('dueDate', true, false);
                                                }}
                                                format="MM/DD/YYYY"
                                                enableAccessibleFieldDOMStructure={false}
                                                slotProps={{
                                                    textField: {
                                                        placeholder: 'MM/DD/YYYY',
                                                        fullWidth: true,
                                                        size: 'medium',
                                                        disabled: isSubmitting,
                                                        error: Boolean(touched.dueDate && errors.dueDate),
                                                        helperText: touched.dueDate ? (errors.dueDate as string) : undefined
                                                    }
                                                }}
                                                minDate={dayjs(minDate)}
                                            />
                                        </Stack>
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <Stack spacing={0.75}>
                                            <InputLabel required>Time</InputLabel>
                                            <TimePicker
                                                value={values.dueTime}
                                                disabled={isSubmitting}
                                                onChange={(newValue) => setFieldValue('dueTime', newValue)}
                                                format="hh:mm A"
                                                enableAccessibleFieldDOMStructure={false}
                                                slotProps={{
                                                    textField: {
                                                        placeholder: 'hh:mm AM/PM',
                                                        fullWidth: true,
                                                        size: 'medium',
                                                        disabled: isSubmitting,
                                                        error: Boolean(touched.dueTime && errors.dueTime),
                                                        helperText: touched.dueTime ? (errors.dueTime as string) : undefined
                                                    }
                                                }}
                                            />
                                        </Stack>
                                    </Grid>

                                    {/* Priority */}
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <Stack spacing={0.75}>
                                            <InputLabel id="priority-label">
                                                Priority
                                            </InputLabel>
                                            <Autocomplete
                                                id="task-priority-autocomplete"
                                                options={priorityOptions}
                                                disabled={isSubmitting}
                                                value={priorityOptions.find((opt) => opt.value === values.priority) || priorityOptions[0]}
                                                onChange={(_, newValue) => {
                                                    if (newValue?.value) {
                                                        setFieldValue('priority', newValue.value);
                                                    }
                                                }}
                                                getOptionLabel={(option) => option.label || ''}
                                                isOptionEqualToValue={(option, val) => option.value === (typeof val === 'string' ? val : val?.value)}
                                                renderOption={(props, option) => {
                                                    const { key, ...restProps } = props as any;
                                                    return (
                                                        <Box component="li" key={key || option.value} {...restProps}>
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                {option.icon}
                                                                <Typography variant="body1">{option.label}</Typography>
                                                            </Stack>
                                                        </Box>
                                                    );
                                                }}
                                                renderInput={(params) => {
                                                    const selectedOpt = priorityOptions.find((opt) => opt.value === values.priority);
                                                    return (
                                                        <TextField
                                                            {...params}
                                                            placeholder="Select Priority..."
                                                            disabled={isSubmitting}
                                                            error={Boolean(touched.priority && errors.priority)}
                                                            helperText={touched.priority ? (errors.priority as string) : undefined}
                                                            slotProps={{
                                                                input: {
                                                                    ...params.InputProps,
                                                                    startAdornment: (
                                                                        <>
                                                                            {selectedOpt?.icon && (
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5, mr: 0.5 }}>
                                                                                    {selectedOpt.icon}
                                                                                </Box>
                                                                            )}
                                                                            {params.InputProps.startAdornment}
                                                                        </>
                                                                    )
                                                                }
                                                            }}
                                                        />
                                                    );
                                                }}
                                                fullWidth
                                                disableClearable
                                            />
                                        </Stack>
                                    </Grid>

                                    {/* Status */}
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <Stack spacing={0.75}>
                                            <InputLabel id="status-label">
                                                Status
                                            </InputLabel>
                                            <Autocomplete
                                                id="task-status-autocomplete"
                                                disabled={isSubmitting}
                                                options={(values.status === 'Cancelled' || values.status === '3') ? statusOptions : statusOptions.filter((opt) => opt.value !== 'Cancelled')}
                                                value={statusOptions.find((opt) => opt.value === values.status) || statusOptions[0]}
                                                onChange={(_, newValue) => {
                                                    if (newValue?.value) {
                                                        setFieldValue('status', newValue.value);
                                                    }
                                                }}
                                                getOptionLabel={(option) => option.label || ''}
                                                isOptionEqualToValue={(option, val) => option.value === (typeof val === 'string' ? val : val?.value)}
                                                renderOption={(props, option) => {
                                                    const { key, ...restProps } = props as any;
                                                    return (
                                                        <Box component="li" key={key || option.value} {...restProps}>
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                {option.icon}
                                                                <Typography variant="body1">{option.label}</Typography>
                                                            </Stack>
                                                        </Box>
                                                    );
                                                }}
                                                renderInput={(params) => {
                                                    const selectedOpt = statusOptions.find((opt) => opt.value === values.status);
                                                    return (
                                                        <TextField
                                                            {...params}
                                                            placeholder="Select Status..."
                                                            disabled={isSubmitting}
                                                            error={Boolean(touched.status && errors.status)}
                                                            helperText={touched.status ? (errors.status as string) : undefined}
                                                            slotProps={{
                                                                input: {
                                                                    ...params.InputProps,
                                                                    startAdornment: (
                                                                        <>
                                                                            {selectedOpt?.icon && (
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5, mr: 0.5 }}>
                                                                                    {selectedOpt.icon}
                                                                                </Box>
                                                                            )}
                                                                            {params.InputProps.startAdornment}
                                                                        </>
                                                                    )
                                                                }
                                                            }}
                                                        />
                                                    );
                                                }}
                                                fullWidth
                                                disableClearable
                                            />
                                        </Stack>
                                    </Grid>

                                    {/* Assign To (UserLookup) */}
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                                            <UserLookup
                                                name="assignedTo"
                                                value={values.assignedTo}
                                                disabled={isSubmitting}
                                                onChange={(val) => setFieldValue('assignedTo', val)}
                                                placeholder="Select users..."
                                                showAddButton={false}
                                                label="Assign To User"
                                                multiple
                                                fullWidth
                                            />
                                        </Stack>
                                    </Grid>

                                    {/* Stage (only for Project tasks) */}
                                    {!isPlanReview && hasProject && (
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Stack spacing={0.75}>
                                                <InputLabel id="stage-label">Stage</InputLabel>
                                                <Autocomplete
                                                    id="task-stage-autocomplete"
                                                    disabled={isSubmitting}
                                                    options={stageOptions}
                                                    value={values.stage || null}
                                                    onChange={(_, newValue) => {
                                                        setFieldValue('stage', newValue || '');
                                                    }}
                                                    getOptionLabel={(option) => (typeof option === 'string' ? option : option || '')}
                                                    isOptionEqualToValue={(option, val) => option === val}
                                                    renderInput={(params) => <TextField {...params} disabled={isSubmitting} placeholder="Select Stage..." />}
                                                    fullWidth
                                                    disableClearable
                                                />
                                            </Stack>
                                        </Grid>
                                    )}

                                    {/* Visible To Contractor */}
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Stack spacing={0.25} sx={{ height: '100%', pt: 0.5 }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <InputLabel sx={{ fontWeight: 400, fontSize: 14, color: 'text.primary' }}>Visible To Contractor</InputLabel>
                                                <Switch
                                                    name="visibleToContractor"
                                                    disabled={isSubmitting}
                                                    checked={values.visibleToContractor}
                                                    onChange={(e) => setFieldValue('visibleToContractor', e.target.checked)}
                                                    color="primary"
                                                    sx={{ my: -0.5 }}
                                                    size='medium'
                                                />
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" fontSize={13} fontWeight={400}>
                                                Allow assigned contractors to see this task
                                            </Typography>
                                        </Stack>
                                    </Grid>

                                    {/* Project Flags (only for Project tasks) */}
                                    {(() => {
                                        if (!hasProject) return null;
                                        const availableFlags = getProjectFlagsForStage(values.stage);
                                        if (availableFlags.length === 0) return null;
                                        return (
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Stack spacing={0.75}>

                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        {availableFlags.map((flag) => {
                                                            const isChecked = Boolean((values.projectFlags as any)?.[flag.key]);
                                                            return (
                                                                <Box
                                                                    key={flag.key}
                                                                    onClick={() => {
                                                                        if (!isSubmitting) setFieldValue(`projectFlags.${flag.key}`, !isChecked);
                                                                    }}
                                                                    sx={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: 1.25,
                                                                        px: 1.5,
                                                                        py: 0.75,
                                                                        borderRadius: '4px',
                                                                        border: '1px solid',
                                                                        borderColor: 'grey.200',
                                                                        bgcolor: isDark ? '#151515' : '#ffffff',
                                                                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                                                        opacity: isSubmitting ? 0.6 : 1,
                                                                        userSelect: 'none',
                                                                        transition: 'all 0.15s ease-in-out',
                                                                        '&:hover': {
                                                                            borderColor: isSubmitting ? 'grey.200' : 'primary.main'
                                                                        }
                                                                    }}
                                                                >
                                                                    <Checkbox
                                                                        checked={isChecked}
                                                                        disabled={isSubmitting}
                                                                        onChange={(e) => setFieldValue(`projectFlags.${flag.key}`, e.target.checked)}
                                                                        icon={<CustomUncheckedIcon />}
                                                                        checkedIcon={<CustomCheckedIcon />}
                                                                        sx={{ p: 0 }}
                                                                    />
                                                                    <Typography variant="body2" sx={{ fontWeight: 400, color: 'text.primary', fontSize: 14 }}>
                                                                        {flag.label}
                                                                    </Typography>
                                                                </Box>
                                                            );
                                                        })}
                                                    </Stack>
                                                </Stack>
                                            </Grid>
                                        );
                                    })()}

                                    {/* Description + File Manager — hamesha side by side */}
                                    <Grid size={{ xs: 12 }}>
                                        <Grid container spacing={2}>
                                            {/* Description (Rich Text Editor) */}
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Stack spacing={0.75}>

                                                    <Box sx={{ width: '100%', opacity: isSubmitting ? 0.7 : 1, pointerEvents: isSubmitting ? 'none' : 'auto' }}>
                                                        <QuillEditor
                                                            value={values.description}
                                                            onChange={(content) => setFieldValue('description', content)}
                                                            placeholder="Enter Description..."
                                                            editorMaxHeight={hasProject ? '155px' : '215px'}
                                                            editorMinHeight={hasProject ? 250 : 300}
                                                            hideImage={true}
                                                            hideTable={true}
                                                            hideLink={true}
                                                            hideVideo={true}
                                                        />
                                                    </Box>
                                                </Stack>
                                            </Grid>

                                            {/* Compressed File Manager Section */}
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Stack spacing={0.75}>
                                                    <Box sx={{ position: 'relative' }}>
                                                        <CompressedFilemanager
                                                            ref={fileManagerRef}
                                                            saveMode="onSaveClick"
                                                            minHeight={hasProject ? 300 : 340}
                                                            saving={isSubmitting}
                                                            module={FileManagerModule.TASK}
                                                            referenceID={task?.taskID ? Number(task.taskID) : undefined}
                                                            initialFiles={formattedInitialFiles}
                                                            allowUpload={true}
                                                            allowDelete={true}
                                                            allowRename={false}
                                                            allowNotes={false}
                                                            allowEditImage={false}
                                                            hideDirectoryHeader={true}
                                                            hideCheckbox={true}
                                                            viewPDF={false}
                                                            onChange={(info) => {
                                                                setHasFileChanges((info.addedCount || 0) > 0 || (info.deletedCount || 0) > 0 || (info.renamedCount || 0) > 0);
                                                            }}
                                                            onSave={async (data, onProgress) => {
                                                                setPendingFiles(data.uploadedFiles || []);
                                                                const targetId = activeTaskIdRef.current || Number(task?.taskID);
                                                                if (targetId) {
                                                                    if (data.deletedFiles && data.deletedFiles.length > 0) {
                                                                        await deleteTaskFiles(targetId, data.deletedFiles, existingFiles);
                                                                    }
                                                                    if (data.uploadedFiles && data.uploadedFiles.length > 0) {
                                                                        await uploadTaskFiles(targetId, data.uploadedFiles, onProgress);
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </Box>

                                                </Stack>
                                            </Grid>

                                        </Grid>
                                    </Grid>
                                </Grid>
                            </LocalizationProvider>
                        )}
                    </DialogContent>
                    <Divider />

                    {/* Footer Actions */}
                    <DialogActions sx={{ p: 2.5 }}>
                        <Grid container justifyContent="space-between" alignItems="center" sx={{ width: 1 }}>
                            <Grid>
                                {Boolean(
                                    task?.createdBy ||
                                    task?.updatedBy ||
                                    task?.completedByUserName ||
                                    (task as any)?.completedByUserName ||
                                    (task as any)?.createdByUserName ||
                                    (task as any)?.updatedByUserName
                                ) && (
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '11px' }}>
                                            {values.status === 'Completed' || getStatusName(values.status) === 'Completed'
                                                ? 'Completed by'
                                                : ((task?.updatedBy?.authorName || (task as any)?.updatedByUserName) ? 'Edited by' : 'Created by')}{' '}
                                            <Typography component="span" sx={{ fontSize: '11px', fontStyle: 'italic', color: 'primary.main' }}>
                                                {values.status === 'Completed' || getStatusName(values.status) === 'Completed'
                                                    ? (task?.completedByUserName || (task as any)?.completedByUserName || task?.updatedBy?.authorName || (task as any)?.updatedByUserName || task?.createdBy?.authorName || (task as any)?.createdByUserName)
                                                    : (task?.updatedBy?.authorName || (task as any)?.updatedByUserName || task?.createdBy?.authorName || (task as any)?.createdByUserName)}
                                            </Typography>{' '}
                                            on{' '}
                                            <Typography component="span" sx={{ fontSize: '11px', fontStyle: 'italic', color: 'primary.main' }}>
                                                {GetFormattedDateTime(
                                                    (values.status === 'Completed' || getStatusName(values.status) === 'Completed')
                                                        ? (task?.completedOn || (task as any)?.completedOn || task?.updatedBy?.updatedDate || (task as any)?.updatedOn || task?.createdBy?.createdDate || (task as any)?.createdOn || '')
                                                        : (task?.updatedBy?.updatedDate || (task as any)?.updatedOn || task?.createdBy?.createdDate || (task as any)?.createdOn || '')
                                                )}
                                            </Typography>
                                        </Typography>
                                    )}
                            </Grid>
                            <Grid >
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={isSubmitting || (!dirty && !hasFileChanges)}
                                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />}
                                    sx={{ px: 3 }}
                                >
                                    {isSubmitting ? 'Saving & Uploading...' : 'Save'}
                                </Button>
                            </Grid>
                        </Grid>
                    </DialogActions>
                </Form>
            </FormikProvider>
        </Dialog>
    );
}
